/**
 * Tunnel photo data model and persistence.
 *
 * Metadata (placement, direction, size) is stored in localStorage (tiny).
 * Image blobs are stored in IndexedDB (supports large files).
 */

import { storageKey } from "./projectConfig";

/** Photo metadata — lightweight, stored in localStorage */
export interface TunnelPhotoMeta {
  id: string;
  /** Station along centerline (meters) */
  station: number;
  /** Hit point on tunnel surface in tunnel-local coords (for display/legacy) */
  hitU: number;
  hitV: number;
  /** Hit point in world-space (exact position for decal placement) */
  hitX: number;
  hitY: number;
  hitZ: number;
  /** Camera direction at capture (world-space, normalized) */
  dirX: number;
  dirY: number;
  dirZ: number;
  /** Decal size in meters (width, height, depth) — legacy, used as fallback */
  size: number;
  /** Camera projection*view matrix at capture (16 floats) — for projective texturing */
  projViewMatrix?: number[];
  /** Camera world position at capture */
  camX?: number;
  camY?: number;
  camZ?: number;
  /** Timestamp */
  createdAt: number;
}

/** Full photo with image data — used at runtime after loading from IndexedDB */
export interface TunnelPhoto extends TunnelPhotoMeta {
  /** Image as data URL (loaded from IndexedDB) */
  dataUrl: string;
}

const LEGACY_META_KEY = "e16-tunnel:photos-meta";
const LEGACY_DB_NAME = "e16-tunnel-photos";
const DB_VERSION = 1;
const STORE_NAME = "images";

function metaKey(projectId: string): string {
  return storageKey(projectId, "photos-meta");
}

function dbName(projectId: string): string {
  return `${projectId}:tunnel-photos`;
}

// ── localStorage: metadata only ──

export function loadPhotoMetas(projectId: string): TunnelPhotoMeta[] {
  try {
    const key = metaKey(projectId);
    // Migrate legacy data
    if (!localStorage.getItem(key)) {
      const legacy = localStorage.getItem(LEGACY_META_KEY);
      if (legacy) localStorage.setItem(key, legacy);
    }
    const raw = localStorage.getItem(key);
    if (!raw) return [];
    return JSON.parse(raw) as TunnelPhotoMeta[];
  } catch {
    return [];
  }
}

export function savePhotoMetas(projectId: string, metas: TunnelPhotoMeta[]): void {
  localStorage.setItem(metaKey(projectId), JSON.stringify(metas));
}

// ── IndexedDB: image blobs ──

function openDB(projectId: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName(projectId), DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

/** Open the legacy DB (for migration) */
function openLegacyDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(LEGACY_DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

export async function savePhotoImage(projectId: string, id: string, dataUrl: string): Promise<void> {
  const db = await openDB(projectId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).put(dataUrl, id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function loadPhotoImage(projectId: string, id: string): Promise<string | null> {
  const db = await openDB(projectId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const req = tx.objectStore(STORE_NAME).get(id);
    req.onsuccess = () => resolve(req.result ?? null);
    req.onerror = () => reject(req.error);
  });
}

export async function deletePhotoImage(projectId: string, id: string): Promise<void> {
  const db = await openDB(projectId);
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readwrite");
    tx.objectStore(STORE_NAME).delete(id);
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

/** Load all photos (metadata + images from IndexedDB). Returns only those with images found. */
export async function loadAllPhotos(projectId: string): Promise<TunnelPhoto[]> {
  const metas = loadPhotoMetas(projectId);
  if (metas.length === 0) return [];

  const db = await openDB(projectId);
  const results: TunnelPhoto[] = [];

  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, "readonly");
    const store = tx.objectStore(STORE_NAME);

    let pending = metas.length;
    for (const meta of metas) {
      const req = store.get(meta.id);
      req.onsuccess = () => {
        if (req.result) {
          results.push({ ...meta, dataUrl: req.result });
        }
        if (--pending === 0) resolve(results);
      };
      req.onerror = () => {
        if (--pending === 0) resolve(results);
      };
    }
  });
}

/** Read a File as a data URL */
export function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

// ── Migration: move old localStorage photos to IndexedDB ──

export async function migrateFromLocalStorage(projectId: string): Promise<TunnelPhoto[]> {
  const OLD_KEY = "e16-tunnel:photos-3d";
  const raw = localStorage.getItem(OLD_KEY);
  if (!raw) return [];

  try {
    const oldPhotos = JSON.parse(raw) as TunnelPhoto[];
    if (oldPhotos.length === 0) return [];

    // Save metadata
    const metas: TunnelPhotoMeta[] = oldPhotos.map(({ dataUrl: _, ...meta }) => meta);
    savePhotoMetas(projectId, metas);

    // Save images to IndexedDB
    for (const photo of oldPhotos) {
      await savePhotoImage(projectId, photo.id, photo.dataUrl);
    }

    // Remove old key
    localStorage.removeItem(OLD_KEY);
    console.log(`Migrated ${oldPhotos.length} photos from localStorage to IndexedDB`);
    return oldPhotos;
  } catch {
    localStorage.removeItem(OLD_KEY);
    return [];
  }
}
